import ky, { Options as KyOptions } from "ky";
import qs from "qs";
import { v4 as uuid } from "uuid";

import {
    AbortSignalManager,
    getUserFromStorage,
    RelodeApiError,
    RelodeApiResponse,
    removeUserFromStorage,
    telemetryService,
} from "..";
import { Routing } from "../domain";

type ApiMethod = "GET" | "PUSH" | "PUT" | "DELETE";
type QueryType =
    | string
    | {
          [key: string]: string | number | boolean;
      }
    | (string | number | boolean)[][]
    | URLSearchParams;

/**
 * Api Options to control stuff
 */
export interface ApiOptions {
    /** number of times to retry a call.  defaults to 4 */
    retries?: number;

    /** abort signal */
    signal?: AbortSignal;

    /** if true and an abort is encountered will throw that can be caught.
     * Otherwise it will simply return undefined.  defaults to false */
    throwOnAbort?: boolean;

    /** number of milliseconds to wait before timing out.
     * Default is 30,000 (30 seconds).
     * In development mode timeout is turned off */
    timeout?: number | false;
    contentType?: string;
}

const defaultApiOptions = (): ApiOptions => ({
    retries: 0,
    signal: null,
    throwOnAbort: false,
    timeout: appConfig.defaultApiTimeoutMs,
});

const toQueryString = (params: string | object) => {
    if (typeof params === "string") return params;
    return qs.stringify(params, {
        skipNulls: true,
        sort: (a, b) => a.localeCompare(b),
    });
};

const cleanUrl = (url: string) => {
    if (url.length > 0 && url[0] === "/") {
        return url.substring(1);
    }
    return url;
};

type TrackData = false | undefined | { name: string; data?: string | object };
export class Api {
    private _options: ApiOptions = { ...defaultApiOptions() };
    private _url: string;
    private _body: any = undefined;
    private _query: QueryType = undefined;
    private _token: string = undefined;
    private _trackData: TrackData;
    private _requestId: string;

    private constructor(url: string) {
        this._url = cleanUrl(url);
        this._requestId = uuid();
    }

    static url = (url: string) => new Api(url);

    public options = (
        options: ApiOptions,
        mergeWithDefault: boolean = true
    ) => {
        if (mergeWithDefault) {
            this._options = { ...defaultApiOptions, ...options };
        } else {
            this._options = options;
        }
        return this;
    };

    public useSignal = (signal?: string | AbortSignal) => {
        const _signal =
            signal instanceof AbortSignal
                ? signal
                : AbortSignalManager.getFresh(signal ?? this._url);
        this._options = {
            ...this._options,
            signal: _signal,
        };
        return this;
    };

    public useToken = (token: string) => {
        this._token = token;
        return this;
    };

    public timeout = (timeout: number) => {
        this._options = {
            ...this._options,
            timeout,
        };
        return this;
    };

    public retries = (retries: number = 0) => {
        this._options = {
            ...this._options,
            retries,
        };
        return this;
    };

    public track = (trackData: TrackData) => {
        this._trackData = trackData;
        return this;
    };

    public throwOnAbort = (shouldThrow: boolean = true) => {
        this._options = {
            ...this._options,
            throwOnAbort: shouldThrow,
        };
        return this;
    };

    public body = (body: any) => {
        this._body = body;
        return this;
    };

    public query = (query: QueryType) => {
        this._query = query;
        return this;
    };

    public get = async <T = unknown>(query?: QueryType): Promise<T> => {
        try {
            this.trackApiCall({ query: query ?? this._query });
            const response: RelodeApiResponse<T> = await ky
                .get(this._url, {
                    ...this.getKyOptions(),
                    searchParams: toQueryString(query ?? this._query),
                })
                .json();

            return response.result;
        } catch (err) {
            return await this.handleError(err);
        }
    };

    put = async <T = unknown>(body?: any): Promise<T> => {
        try {
            this.trackApiCall(body ?? this._body);
            const response: RelodeApiResponse<T> = await ky
                .put(this._url, {
                    ...this.getKyOptions(),
                    ...this.getBody(body),
                })
                .json();

            return response.result;
        } catch (err) {
            return await this.handleError(err, body ?? this._body);
        }
    };

    post = async <T = unknown>(body?: any): Promise<T> => {
        try {
            this.trackApiCall(body ?? this._body);
            const response: RelodeApiResponse<T> = await ky
                .post(this._url, {
                    ...this.getKyOptions(),
                    ...this.getBody(body),
                })
                .json();

            return response.result;
        } catch (err) {
            return await this.handleError(err, body ?? this._body);
        }
    };

    call = async <T = unknown>(method: ApiMethod): Promise<T> => {
        try {
            this.trackApiCall({ query: this._query, body: this._body });
            const response: RelodeApiResponse<T> = await ky(this._url, {
                ...this.getKyOptions(),
                ...this.getBody(),
                method,
                searchParams: this._query,
            }).json();

            return response.result;
        } catch (err) {
            return await this.handleError(err, this._body);
        }
    };

    private getBody = (body?: any) => {
        body = body ?? this._body;
        if (!body) return;

        if (body instanceof FormData) {
            return { body };
        }
        if (typeof body === "string") {
            return { body };
        }

        return { json: body };
    };

    private getContentType = (body?: any) => {
        if (!body) return { "Content-Type": "application/json" };
    };

    private trackApiCall = (data: string | object) => {
        const kyOptions = this.getKyOptions();
        const convertData = data => {
            if (typeof data === "string") {
                return {
                    data,
                    requestId: this._requestId,
                    url: this._url,
                };
            }
            return {
                ...data,
                requestId: this._requestId,
                url: this._url,
            };
        };

        if (this._trackData === undefined) {
            this._trackData = {
                name: `${appConfig.apiBaseUrl}/${this._url}`,
                data: data,
            };
        }

        if (this._trackData === false) {
            this._trackData = {
                name: `${appConfig.apiBaseUrl}/${this._url}`,
                data: "false",
            };
        }

        this._trackData.data = convertData(this._trackData.data);
        this._trackData.name = `API: ${this._trackData.name}`;
        telemetryService.trackEvent(this._trackData.name, {
            data: this._trackData.data,
            kyOptions,
        });
    };

    private getKyOptions = (): KyOptions => {
        const user = getUserFromStorage();
        const token = this._token || (!!user ? user.token : null);
        // let { timeout } = this._options;
        // if (process.env.NODE_ENV !== "production") {
        //     // no timeout when developing locally
        //     timeout = false;
        // }

        // Disable client-side timeouts and let the API timeout
        const timeout = false;

        const kyOptions: KyOptions = {
            retry: {
                limit: Math.max(this._options.retries, 0),
            },

            signal: this._options.signal,
            prefixUrl: appConfig.apiBaseUrl,
            timeout: timeout,
            headers: {
                Authorization: `Bearer ${token}`,
            },
            hooks: {
                beforeRequest: [
                    (request, options) => {
                        if (!(options.body instanceof FormData)) {
                            request.headers.set(
                                "content-type",
                                "application/json"
                            );
                        }
                        return request;
                    },
                ],
            },
        };

        return kyOptions;
    };

    handleError = async (err: any, body?: any) => {
        if (!appConfig.disableRawApiErrors) {
            const isTypeError = err instanceof TypeError;

            telemetryService.trackException(err, {
                type: "RAW_API_ERROR",
                requestId: this._requestId,
                url: this._url,
                isTypeError: isTypeError ? "true" : "false",
                baseType: typeof err,
                message: err.message,
                stack: err.stack,
            });
        }
        const apiError = await RelodeApiError.create(
            err,
            this._url,
            this._requestId,
            body,
            this._trackData as { name: string; data: { [name: string]: any } }
        );

        switch (apiError.status) {
            case 401: {
                const msg = `API: call returned 401.  Redirecting to sign in.  url: ${this._url}`;
                const user = getUserFromStorage();
                telemetryService.trackEvent("API: 401 ", {
                    data: msg,
                    user: user,
                });

                removeUserFromStorage();
                localStorage.setItem(
                    "relode:redirectUrl",
                    window.location.href
                );

                // local user has been removed and redirect set to bring user back to where they were
                window.location.assign(Routing.Account.SIGNIN + "/expire");

                // reject with abort so that callers don't receive a resolved undefined which may cause them to error
                apiError.type = "abort";
                return Promise.reject(apiError);
            }
        }

        if (apiError.type === "abort" && !this._options.throwOnAbort) {
            console.warn(
                `api call aborted.  resolving undefined.  url: ${this._url}`
            );
            return Promise.resolve(undefined);
        }

        return Promise.reject(apiError);
    };
}
